import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/blocks/PageHero';
import { ServicesGrid } from '../components/blocks/ServicesGrid';
import { CheckList } from '../components/blocks/CheckList';
import { FaqSection } from '../components/blocks/FaqSection';
import { SectionHeading } from '../components/blocks/SectionHeading';
import { SectionDivider, SURFACE } from '../components/blocks/SectionDivider';
import { useSeo } from '../lib/useSeo';
import { serviceSchema, faqSchema } from '../lib/schema';
import { servicesData } from '../data/services';

/**
 * PAGE: /start-your-journey
 * WORDPRESS: page template `page-start-your-journey.php` (service CPT archive)
 *
 * Blocks: hero → services_grid → prose + checklist → faq
 */

const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/rHtZs4vfkDTwbmXz6uzL7E/milangela_start_your_journey.webp';

/** ACF: repeater `support_points` */
const supportPoints = ['long for greater balance and well-being', 'feel emotionally or physically overwhelmed', 'wish to slow down and reconnect with yourself', 'are navigating change or seeking a new perspective', 'want to deepen your self-awareness and personal growth', 'are looking for a safe, supportive space to simply be'];

/** ACF: repeater `page_faqs` — the comparison questions AI engines get asked. */
const faqs = [{
  question: 'Which path should I choose?',
  answer: 'Choose Meditation Between Worlds if you want an ongoing guided practice of presence and awareness. Choose Sound-Infused Renewal if your body is asking for rest and rebalancing through Biofield Tuning. Choose Sopotje if what you need most is to be heard, with compassionate conversation and space to reflect.'
}, {
  question: 'What do sessions cost?',
  answer: 'Meditation Between Worlds is €15 per session or €50 for a monthly pass. Sopotje guidance sessions are €60 per hour. A full Sound-Infused Renewal Biofield Tuning session is €100 and includes consultation, session and feedback.'
}, {
  question: 'Are sessions available online?',
  answer: 'Meditation Between Worlds and Sopotje are offered both in person and online. Sound-Infused Renewal is normally an in-studio session.'
}, {
  question: 'Is any of this a substitute for medical care?',
  answer: 'No. These are complementary wellbeing practices, not medical or psychological treatment. They do not diagnose, treat or cure any condition, and they are not a replacement for care from a qualified health professional.'
}];
export function Services() {
  useSeo({
    title: 'Start Your Journey',
    description: 'Three gentle paths for inner balance and emotional clarity: guided meditation, Biofield Tuning sound therapy and Sopotje guidance sessions.',
    path: '/start-your-journey',
    image: HERO_IMAGE,
    breadcrumbs: [{
      name: 'Start Your Journey',
      path: '/start-your-journey'
    }],
    schema: [...servicesData.map(service => serviceSchema({
      name: service.title,
      description: service.cardSummary,
      path: service.link,
      image: service.image,
      price: service.priceNumeric,
      serviceType: service.shortTitle
    })), faqSchema(faqs)]
  });
  return <div className="min-h-screen bg-cream" data-id="element-589" data-mp="29wrg7rrcb9bs">
      <Header data-id="element-590" data-mp="2eo9g0chmn3v6" />

      <main data-id="element-591" data-mp="2eorkfweg0oe6">
        <PageHero eyebrow="A gentle first step" heading="Start Your Journey" subheading="Every garden grows differently. So does every person." body="Whether you are looking for inner balance, emotional clarity, or simply a place to pause, these three paths offer different ways of returning to yourself. Explore the one that speaks to where you are today." image={HERO_IMAGE} imageAlt="Milangela meditating by the sea at sunset" dividerFill={SURFACE.cream} data-id="element-592" data-mp="2eoj9ia1vrt5m" />

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-593" data-mp="1nwap21exa1be">
          <ServicesGrid eyebrow="Three ways to begin" heading="Find the path that meets you where you are." services={servicesData} data-id="element-594" data-mp="1ixpn9xk0v9ku" />
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.sage} data-id="element-595" data-mp="1a6gud2gqkmtr" />

        <section className="bg-sage-50 px-6 pb-24 pt-8 md:pb-28" data-id="element-596" data-mp="156ktvd78zdiy">
          <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:gap-20" data-id="element-597" data-mp="157vvp7h4ub7f">
            <SectionHeading eyebrow="A little guidance" heading="Not sure where to begin? You don't have to be." intro={<>
                  <p data-id="element-599" data-mp="r8mgaa0r3l4i">
                    Sometimes we come looking for peace. Sometimes for answers.
                    Sometimes we simply need a space where we can breathe again.
                  </p>
                  <p data-id="element-600" data-mp="2llm4grj75nne">
                    Each offering has its own purpose, yet all are rooted in the
                    same intention: helping you reconnect with yourself through
                    presence, awareness, and compassionate support.
                  </p>
                </>} data-id="element-598" data-mp="109ko73eb8csd" />

            <CheckList lead="You may find one of these paths helpful if you..." items={supportPoints} data-id="element-601" data-mp="h58tnv82gijs" />
          </div>

          <div className="mx-auto mt-16 max-w-3xl text-center" data-id="element-602" data-mp="3kl6321yelmh2">
            <p className="font-serif text-2xl italic leading-relaxed text-charcoal" data-id="element-603" data-mp="3pi6aag6zov9r">
              Sometimes a short conversation is enough to discover which path
              feels right for you.
            </p>
            <Link to="/lets-connect" className="mt-8 inline-flex items-center gap-3 border-b border-charcoal pb-1 text-xs font-medium tracking-[0.16em] text-charcoal uppercase transition-colors hover:border-sage-700 hover:text-sage-700 focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 focus:ring-offset-sage-50" data-id="element-604" data-mp="2f1htyckdxs2">
              <ArrowDown size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-605" data-mp="35v7zbq1eh1yu" />
              Let&apos;s Connect
            </Link>
          </div>
        </section>

        <SectionDivider from={SURFACE.sage} to={SURFACE.cream} data-id="element-606" data-mp="3jtoem5dbw0sl" />

        <section className="bg-cream px-6 pb-24 pt-8 md:pb-28" data-id="element-607" data-mp="3ewek4bb30yxw">
          <div className="mx-auto max-w-3xl" data-id="element-608" data-mp="2r5iv1ly0ghuc">
            <FaqSection eyebrow="Choosing your path" heading="Common questions" faqs={faqs} data-id="element-609" data-mp="2r6bu00qqa31x" />
          </div>
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.charcoal} data-id="element-610" data-mp="30wn9mkjry0on" />
      </main>

      <Footer data-id="element-611" data-mp="2w4db4g4lfsar" />
    </div>;
}